import { createContext, useContext } from "react";

import { ComplexBundle, WordPressProduct } from "../types/Product.ts";
import { Vehicle, VehicleImage } from "../types/Vehicle.ts";

export interface VariantGroup {
  label: string;
  values: Vehicle[];
}

export interface OptionDetail {
  quantity?: number;
}

export interface ValidationError {
  stepIndex: number;
  entryIndex: number;
}

/**
 * Context interface for vehicle selection state and actions
 */
export interface VehicleSelectionContextProps {
  selectedMakeModelId: number | null;
  selectedModel: Vehicle | null;
  selectedImage: VehicleImage | null;
  selectedVehiclePrettyName: string;
  handleMakeModelChange: (value: number | null) => void;
  availableVariants: VariantGroup[];
  selectedVariants: Vehicle[];
  handleVariantChange: (index: number, value: number) => void;
  selectedGvmWeight: string | null;
  handleGvmWeightChange: (value: string | null) => void;
  allGvmUpgrades: WordPressProduct[];
  availableGvmUpgrades: [string, WordPressProduct[]][];
  handleGvmUpgradeChange: (id: number | null) => void;
  selectedGvmUpgrade: WordPressProduct | null;
  steps: ComplexBundle.Step[];
  products: ComplexBundle.Option[];
  productsByGroup: {
    sort: number;
    step?: ComplexBundle.Step;
    entry?: ComplexBundle.Entry;
    options: ComplexBundle.Option[];
  }[];
  pricing: Pricing;
  selectedOptions: Record<string, number[] | number | null>;
  selectedOptionDetails: Record<string, OptionDetail>;
  handleOptionDetailChange: (optionKey: string, value: OptionDetail) => void;
  handleOptionChange: (
    stepIndex: number,
    entryIndex: number,
    optionIndex: number[] | number | null,
  ) => void;
  addToCart: () => Promise<void>;
  orderMode: "new" | "editing";
  validationErrors: ValidationError[];
  setValidationErrors: (errors: ValidationError[]) => void;
  notes: string;
  handleNotesChange: (notes: string) => void;
}

export interface Pricing {
  total: number;
}

export const VehicleSelectionContext = createContext<
  VehicleSelectionContextProps | undefined
>(undefined);
/**
 * Custom hook to access the vehicle selection context
 * @returns {VehicleSelectionContextProps} The vehicle selection context
 * @throws {Error} If used outside of a VehicleSelectionProvider
 */
export const useVehicleSelection = (): VehicleSelectionContextProps => {
  const context = useContext(VehicleSelectionContext);
  if (!context) {
    throw new Error(
      "useVehicleSelection must be used within a VehicleSelectionProvider",
    );
  }
  return context;
};
